import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { debugError } from '../utils/debug';
import { logError } from '../services/errorLoggingService';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log the error to console for debugging
        debugError('ErrorBoundary caught an error:', error, errorInfo);

        // Update state with error info
        this.setState({
            error,
            errorInfo
        });

        // Log to backend API (which logs to Application Insights)
        // Get userId and teamId from sessionStorage if available (set by auth context)
        let userId: string | undefined;
        let teamId: string | undefined;
        try {
            const authState = sessionStorage.getItem('auth_state');
            if (authState) {
                const parsed = JSON.parse(authState);
                if (parsed?.user) {
                    userId = parsed.user.userId;
                    teamId = parsed.user.teamId;
                }
            }
        } catch {
            // Ignore errors getting user info
        }

        // Convert ErrorInfo to the format expected by logError (handle null componentStack)
        logError(error, {
            componentStack: errorInfo.componentStack ?? undefined
        }, userId, teamId);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return <ErrorFallback
                error={this.state.error}
                errorInfo={this.state.errorInfo}
                onRetry={this.handleRetry}
            />;
        }

        return this.props.children;
    }
}

interface ErrorFallbackProps {
    error?: Error;
    errorInfo?: ErrorInfo;
    onRetry: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo, onRetry }) => {
    const theme = useTheme();
    const [isErrorDetailsOpen, setIsErrorDetailsOpen] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    const copyErrorDetailsToClipboard = () => {
        if (!error) return;

        const errorText = [
            'Error Message:',
            error.toString(),
            '',
            error.stack ? 'Stack Trace:' : '',
            error.stack || '',
            '',
            errorInfo?.componentStack ? 'Component Stack:' : '',
            errorInfo?.componentStack || '',
        ].filter(Boolean).join('\n');

        navigator.clipboard.writeText(errorText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch((err) => {
            console.error('Failed to copy to clipboard:', err);
        });
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: theme.spacing.xl,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            border: `1px solid ${theme.colors.border.default}`,
            margin: theme.spacing.lg,
        }}>
            {/* Error Icon */}
            <div style={{
                fontSize: '48px',
                color: theme.colors.status.error,
                marginBottom: theme.spacing.lg,
            }}>
                <i className="fas fa-exclamation-triangle"></i>
            </div>

            {/* Error Title */}
            <h2 style={{
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.text.primary,
                marginBottom: theme.spacing.md,
                textAlign: 'center',
            }}>
                Something went wrong
            </h2>

            {/* Error Message */}
            <p style={{
                fontSize: theme.typography.fontSize.md,
                color: theme.colors.text.secondary,
                marginBottom: theme.spacing.lg,
                textAlign: 'center',
                maxWidth: '500px',
                lineHeight: 1.5,
            }}>
                We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
            </p>

            {/* Retry Button */}
            <button
                onClick={onRetry}
                style={{
                    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.inverse,
                    border: 'none',
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.md,
                    fontWeight: theme.typography.fontWeight.medium,
                    cursor: 'pointer',
                    transition: `background-color ${theme.transitions.normal}`,
                    marginBottom: theme.spacing.lg,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary;
                }}
            >
                <i className="fas fa-redo" style={{ marginRight: theme.spacing.sm }}></i>
                Try Again
            </button>

            {/* Refresh Page Button */}
            <button
                onClick={() => window.location.reload()}
                style={{
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    backgroundColor: 'transparent',
                    color: theme.colors.primary,
                    border: `1px solid ${theme.colors.primary}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    cursor: 'pointer',
                    transition: `all ${theme.transitions.normal}`,
                    marginBottom: theme.spacing.lg,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.colors.primary;
                    e.currentTarget.style.color = theme.colors.text.inverse;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = theme.colors.primary;
                }}
            >
                <i className="fas fa-refresh" style={{ marginRight: theme.spacing.sm }}></i>
                Refresh Page
            </button>

            {/* Error Details - Collapsible Section */}
            {error && (
                <div style={{
                    marginTop: theme.spacing.lg,
                    maxWidth: '600px',
                    width: '100%',
                }}>
                    <button
                        onClick={() => setIsErrorDetailsOpen(!isErrorDetailsOpen)}
                        style={{
                            width: '100%',
                            padding: theme.spacing.md,
                            backgroundColor: theme.colors.surfaceSecondary,
                            border: `1px solid ${theme.colors.border.default}`,
                            borderRadius: theme.borderRadius.md,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.text.primary,
                            transition: `background-color ${theme.transitions.normal}`,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.colors.surface;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                        }}
                    >
                        <span>Error Details</span>
                        <i
                            className={`fas fa-chevron-${isErrorDetailsOpen ? 'up' : 'down'}`}
                            style={{
                                fontSize: theme.typography.fontSize.xs,
                                transition: 'transform 0.2s ease',
                            }}
                        ></i>
                    </button>
                    {isErrorDetailsOpen && (
                        <div style={{
                            marginTop: theme.spacing.xs,
                            padding: theme.spacing.md,
                            backgroundColor: theme.colors.surfaceSecondary,
                            borderRadius: theme.borderRadius.md,
                            border: `1px solid ${theme.colors.border.default}`,
                            fontSize: theme.typography.fontSize.sm,
                            fontFamily: 'monospace',
                            color: theme.colors.text.secondary,
                            whiteSpace: 'pre-wrap',
                            overflow: 'auto',
                            maxHeight: '400px',
                        }}>
                            <div style={{ marginBottom: theme.spacing.sm }}>
                                <strong style={{ color: theme.colors.text.primary }}>Error Message:</strong>
                                <div style={{ marginTop: theme.spacing.xs }}>
                                    {error.toString()}
                                </div>
                            </div>
                            {error.stack && (
                                <div style={{ marginBottom: theme.spacing.sm }}>
                                    <strong style={{ color: theme.colors.text.primary }}>Stack Trace:</strong>
                                    <pre style={{
                                        marginTop: theme.spacing.xs,
                                        fontSize: theme.typography.fontSize.xs,
                                        overflow: 'auto',
                                    }}>
                                        {error.stack}
                                    </pre>
                                </div>
                            )}
                            {errorInfo && errorInfo.componentStack && (
                                <div>
                                    <strong style={{ color: theme.colors.text.primary }}>Component Stack:</strong>
                                    <pre style={{
                                        marginTop: theme.spacing.xs,
                                        fontSize: theme.typography.fontSize.xs,
                                        overflow: 'auto',
                                    }}>
                                        {errorInfo.componentStack}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}
                    {isErrorDetailsOpen && (
                        <button
                            onClick={copyErrorDetailsToClipboard}
                            style={{
                                marginTop: theme.spacing.sm,
                                width: '100%',
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                backgroundColor: theme.colors.surfaceSecondary,
                                color: theme.colors.text.primary,
                                border: `1px solid ${theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.md,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.medium,
                                cursor: 'pointer',
                                transition: `all ${theme.transitions.normal}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing.sm,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = theme.colors.surface;
                                e.currentTarget.style.borderColor = theme.colors.primary;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                                e.currentTarget.style.borderColor = theme.colors.border.default;
                            }}
                        >
                            <i className={`fas fa-${copied ? 'check' : 'copy'}`}></i>
                            {copied ? 'Copied!' : 'Copy to clipboard'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ErrorBoundary;
